import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SyncIcon from '@mui/icons-material/Sync';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ColumnSectionHeader from '../ColumnSectionHeader';
import { BUILD_SOURCES_SUBTITLE, UPDATE_SOURCES_SUBTITLE } from '../constants';
import DocumentUploadZone from './DocumentUploadZone';
import DocumentList from './DocumentList';
import UrlAddForm from './UrlAddForm';
import UrlList from './UrlList';

const SourcesColumn = ({
  mode,
  sourcesLocked = false,
  documents,
  urls,
  onUploadFile,
  onAddUrl,
  onRemoveSource,
  onToggleSharing,
  onPrimaryAction,
  isAddingUrl,
  isPrimaryLoading,
  removingId,
  sharingId,
  isPrimaryDisabled = false,
  primaryDisabledReason = '',
  onRetrySource,
  retryingId,
  readyCount = 0,
  failedCount = 0,
  hidePrimary = false,
}) => {
  const isUpdate = mode === 'built';
  const hasDocuments = documents.length > 0;

  // The count only appears once sources are actually being left out, so the
  // happy path stays uncluttered.
  const primaryLabel = isUpdate
    ? 'Update Company Profile'
    : `Build Company Profile${failedCount > 0 ? ` (${readyCount} sources)` : ''}`;

  const primaryButton = (
    <Button
      fullWidth
      variant='contained'
      disabled={isPrimaryLoading || isPrimaryDisabled}
      startIcon={isUpdate ? <SyncIcon /> : <AutoAwesomeIcon />}
      onClick={onPrimaryAction}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 1.5,
        py: 1.25,
        boxShadow: 'none',
      }}
    >
      {primaryLabel}
    </Button>
  );

  return (
    <Box
      border={1}
      borderColor='divider'
      borderRadius={2}
      p={2.5}
      display='flex'
      flexDirection='column'
      minHeight={{ md: 640 }}
      bgcolor='#fff'
    >
      <ColumnSectionHeader
        number={1}
        title={isUpdate ? 'Manage your information' : 'Add your information'}
        subtitle={isUpdate ? UPDATE_SOURCES_SUBTITLE : BUILD_SOURCES_SUBTITLE}
      />

      <Typography variant='subtitle2' fontWeight={700} mb={1}>
        Documents ({documents.length})
      </Typography>
      <DocumentUploadZone
        compact={hasDocuments}
        disabled={sourcesLocked}
        onUploaded={onUploadFile}
        currentFileCount={documents.length}
        isUpdate={isUpdate}
      />
      <DocumentList
        documents={documents}
        onRemove={sourcesLocked ? undefined : onRemoveSource}
        removingId={removingId}
        showSharing={isUpdate}
        onToggleSharing={onToggleSharing}
        sharingId={sharingId}
        onRetry={onRetrySource}
        retryingId={retryingId}
      />

      <Typography variant='subtitle2' fontWeight={700} mt={2.5} mb={1}>
        URLs ({urls.length})
      </Typography>
      <UrlAddForm onAdd={onAddUrl} isAdding={isAddingUrl} disabled={sourcesLocked} />
      <UrlList
        urls={urls}
        onRemove={sourcesLocked ? undefined : onRemoveSource}
        removingId={removingId}
        showSharing={isUpdate}
        onToggleSharing={onToggleSharing}
        sharingId={sharingId}
        onRetry={onRetrySource}
        retryingId={retryingId}
      />

      <Box mt={2.5} display='flex' alignItems='center' gap={1}>
        <FormControlLabel
          control={<Checkbox checked={false} disabled />}
          label={<Typography variant='body2'>Include public web research</Typography>}
        />
        <Chip label='Coming soon' size='small' />
      </Box>

      <Box mt='auto' pt={3}>
        {failedCount > 0 && (
          <Alert
            severity='warning'
            sx={{ mb: 2, py: 0.75, alignItems: 'flex-start', '& .MuiAlert-message': { py: 0.25 } }}
          >
            <Typography variant='caption' fontWeight={700} sx={{ display: 'block' }}>
              {failedCount === 1 ? '1 source failed' : `${failedCount} sources failed`}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              Failed sources will not contribute to the generated profiles. Build uses the {readyCount}{' '}
              successfully processed {readyCount === 1 ? 'source' : 'sources'}.
            </Typography>
          </Alert>
        )}

        {!hidePrimary &&
          (isPrimaryDisabled && primaryDisabledReason ? (
            <Tooltip title={primaryDisabledReason} arrow placement='top'>
              <span style={{ display: 'block' }}>{primaryButton}</span>
            </Tooltip>
          ) : (
            primaryButton
          ))}

        <Box display='flex' alignItems='flex-start' gap={1} mt={1.5}>
          <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.15 }} />
          <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            Your information stays private.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SourcesColumn;
